import type {
  OneTimePaymentScheduleReviewComponent,
  OneTimePaymentScheduleReviewComponentWrapper,
} from '@/src/app/main/(tabs)/card/card-management/payment/oneTimePayment/[id]'
import { formatToTextMonth } from '@/src/common/utils/date'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks'
import { renderPaymentScheduleAmount } from '@/src/experiences/card-activity/components/PaymentScheduleRow'
import { SchedulePaymentsApi } from '@/src/experiences/card-payments/store/api'
import type { ComponentProps } from 'react'
import { formatPaymentProfile } from '@/src/experiences/card-payments/formatPaymentProfile'
import { capitalize } from 'lodash'
import { Api } from '@/src/store/api'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import * as CardPaymentsConstants from '@/src/experiences/card-payments/constants'
import { router } from 'expo-router'
import * as Routes from '@/src/common/enums/routes'
import { useLogError } from '@/src/common/hooks/useLogError'
type CardAccountId = NonNullable<ReturnType<typeof useCardAccount>['data']>[number]['id']

export const PlatformOneTimePaymentScheduleReview = ((props) => {
  const query = useCardAccount()

  if (query.data?.[0]?.id) {
    return (
      <PlatformOneTimePaymentScheduleReviewWithCardAccount
        {...props}
        cardAccountId={query.data[0].id}
        initialized={!query.isUninitialized}
        loading={query.isLoading}
      />
    )
  }

  return (
    <props.OneTimePaymentScheduleReview
      {...props}
      initialized={!query.isUninitialized}
      loading={query.isLoading}
    />
  )
}) satisfies OneTimePaymentScheduleReviewComponentWrapper

function PlatformOneTimePaymentScheduleReviewWithCardAccount(
  props: ComponentProps<OneTimePaymentScheduleReviewComponent> &
    ComponentProps<OneTimePaymentScheduleReviewComponentWrapper> & {
      cardAccountId: CardAccountId
    },
) {
  const { OneTimePaymentScheduleReview, id, cardAccountId } = props
  const logError = useLogError()

  const query = SchedulePaymentsApi.endpoints[
    'GET /card/:cardAccountId/payments/schedules'
  ].useQuery({ params: { cardAccountId } })
  const [deletePaymentSchedule, { isLoading: isDeletingPaymentSchedule }] =
    Api.endpoints['DELETE /card/:cardAccountId/payments/schedules/:scheduleId'].useMutation()

  const Alert = useAlert(PlatformOneTimePaymentScheduleReviewWithCardAccount.name)
  const paymentSchedule = query.data?.find((schedule) => schedule.id === id)

  return (
    <OneTimePaymentScheduleReview
      {...props}
      initialized={props.initialized || !query.isUninitialized}
      loading={props.loading || query.isLoading}
      data={
        paymentSchedule
          ? {
              amount: renderPaymentScheduleAmount(paymentSchedule),
              status: capitalize(paymentSchedule.status),
              date: formatToTextMonth(new Date(paymentSchedule.processDateTime), {}),
              account: formatPaymentProfile(paymentSchedule.profile),
            }
          : undefined
      }
      cancel={() => {
        if (!paymentSchedule) return
        Alert.alert(
          'Cancellation Notice',
          'Cancellation must be received by 9:00 AM Eastern on the date your payment is scheduled.',
          [
            { text: 'Back', onPress: () => {}, style: 'cancel' },
            {
              text: CardPaymentsConstants.Strings.CancelOneTimePayment,
              onPress() {
                void deletePaymentSchedule({
                  params: {
                    cardAccountId,
                    scheduleId: paymentSchedule.id,
                  },
                }).then((response) => {
                  if (response.error) {
                    logError('Failed to delete payment schedule', response.error)
                    Alert.alert('Failed to delete payment schedule', 'Please try again later.')
                  } else {
                    Alert.alert(
                      CardPaymentsConstants.Strings.OneTimePaymentSuccessfullyCanceled,
                      'You will no longer be charged on the date of your scheduled payment.',
                      [
                        {
                          text: CardPaymentsConstants.Strings.OneTimePaymentSuccessfullyCanceledOk,
                          onPress: () => {},
                        },
                      ],
                      {
                        onDismiss: () => {
                          router.replace(Routes.Routes.CardManagement.route)
                        },
                      },
                    )
                  }
                })
              },

              style: 'destructive',
            },
          ],
        )
      }}
      disabled={!!isDeletingPaymentSchedule}
    />
  )
}
